import { Tag, Tooltip, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { FUNCTION_COLORS, isOverdue, personName } from './model';

/**
 * One person on one function. Dashed = helps; a hand-over is filled in the
 * theme's warning colour for the one leaving (→) and its success colour for
 * the one coming (←); "2/7" = covers only some of the entity's
 * companies, struck through = the person left HLB, cyan = covers for an
 * absent colleague, red with "!" = the hand-over's end date has passed. `dim` fades it when another
 * person is selected.
 */
export default function AssignmentChip({ entry, peopleById, showFunction = false, asFunction = false, dim = false }) {
  const { t } = useTranslation('teamAssignments');
  const { token } = theme.useToken();
  const person = peopleById.get(entry.employee);
  const partner = entry.transitionPartner ? peopleById.get(entry.transitionPartner) : null;
  const helper = entry.role === 'HELPER';
  const partial = entry.total > 1 && entry.count < entry.total;

  const left = !!person?.left;
  const cover = !!entry.substitution;
  const overdue = isOverdue(entry);
  // Leaving = orange, coming = green (Bora 17.10), from the platform theme.
  const transColor = entry.transition === 'OUT' ? token.colorWarning : entry.transition === 'IN' ? token.colorSuccess : null;

  const title = [
    showFunction ? null : personName(person),
    left ? t('people.leftHandOver') : null,
    cover ? t('chip.cover') : null,
    overdue ? t('chip.overdue') : null,
    t(`functions.${entry.function}`),
    t(`roles.${entry.role}`),
    entry.transition === 'OUT' && partner ? t('chip.handsOverTo', { name: personName(partner) }) : null,
    entry.transition === 'IN' && partner ? t('chip.takesOverFrom', { name: personName(partner) }) : null,
    entry.transitionUntil ? t('chip.until', { date: new Date(entry.transitionUntil).toLocaleDateString('sr-RS') }) : null,
    partial ? t('chip.partial', { count: entry.count, total: entry.total }) : null,
    ...(entry.notes || (entry.note ? [entry.note] : [])),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip title={title}>
      <Tag
        color={transColor ? undefined : left || overdue ? 'volcano' : cover ? 'cyan' : FUNCTION_COLORS[entry.function]}
        style={{
          borderStyle: helper ? 'dashed' : 'solid',
          background: transColor && !helper ? transColor : helper ? 'transparent' : undefined,
          ...(transColor ? { borderColor: transColor, color: helper ? transColor : token.colorWhite } : {}),
          ...(transColor && overdue ? { boxShadow: `0 0 0 1px ${token.colorError}` } : {}),
          opacity: dim ? 0.38 : 1,
          marginInlineEnd: 4,
          marginBottom: 4,
          whiteSpace: 'normal',
        }}
      >
        {helper && '+ '}
        {showFunction && <b style={{ marginRight: 4 }}>{t(`functionShort.${entry.function}`)}</b>}
        {asFunction ? (
          // One person's own view: the chip names the work, and who it goes to or comes from.
          <span>
            {t(`functions.${entry.function}`)}
            {entry.transition === 'OUT' && ` → ${partner ? personName(partner) : ''}`}
            {entry.transition === 'IN' && ` ← ${partner ? personName(partner) : ''}`}
          </span>
        ) : (
          <>
            <span style={left ? { textDecoration: 'line-through' } : undefined}>{personName(person)}</span>
            {entry.transition === 'OUT' && ' →'}
            {entry.transition === 'IN' && ' ←'}
          </>
        )}
        {cover && <span style={{ opacity: 0.75 }}>{` · ${t('chip.coverShort')}`}</span>}
        {overdue && <span>{' !'}</span>}
        {partial && <span style={{ opacity: 0.7 }}>{` · ${entry.count}/${entry.total}`}</span>}
      </Tag>
    </Tooltip>
  );
}
